import { MetricCard, Section } from "../../../components";
import { formatImpact } from "../../../utils/format";
import type { PortfolioSummaryGroup } from "../api/portfolio";
import { useRangeMode } from "../../impact/context/RangeModeContext";
import { RangeWhisker } from "../../impact/components/RangeVisuals";
import {
  isRangeReady,
  metricRange,
  type EndpointMetric,
  type ScenariosBlock,
} from "../../impact/domain/scenarios";

interface Props {
  totals: PortfolioSummaryGroup | undefined;
  isLoading: boolean;
  scenarios?: ScenariosBlock;
  analysisMode?: "ctg" | "ops";
}

export function PortfolioKPIBar({ totals, isLoading, scenarios, analysisMode = "ctg" }: Props) {
  const { mode: rangeMode } = useRangeMode();
  const showRange = rangeMode === "range" && isRangeReady(scenarios);
  const lens = analysisMode === "ops" ? "ops" : "ctg";

  const kpiWhisker = (metric: EndpointMetric, unit: "dalys" | "pdfs" | "usd") => {
    if (!showRange) return null;
    const r = metricRange(scenarios, lens, metric);
    if (!r) return null;
    return (
      <RangeWhisker
        floor={r.floor}
        central={r.central}
        ceiling={r.ceiling}
        benefit={r.central >= 0}
        format={(n) => formatImpact(n, unit)}
      />
    );
  };
  if (isLoading || !totals) {
    return (
      <Section>
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-5">
          {Array.from({ length: 5 }).map((_, i) => (
            <div key={i} className="h-24 rounded-xl bg-surface-700 animate-pulse" />
          ))}
        </div>
      </Section>
    );
  }

  const mwDisplay = totals.total_mw >= 1000
    ? `${(totals.total_mw / 1000).toFixed(1)} GW`
    : `${Math.round(totals.total_mw)} MW`;

  // A negative aggregate is a genuine net lifecycle burden (e.g. wind
  // displacing an already-clean grid). Surface it instead of hiding it
  // behind abs() — consistent with the project-level impact views.
  const dalysBurden = totals.dalys < 0;
  const pdfsBurden = totals.pdfs < 0;
  const usdBurden = totals.usd < 0;
  const anyBurden = dalysBurden || pdfsBurden || usdBurden;

  return (
    <Section>
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-5">
        <MetricCard
          label="Projects"
          value={totals.project_count}
          variant="quantum"
          icon={<FolderIcon />}
        />
        <MetricCard
          label="Capacity"
          value={mwDisplay}
          variant="ecosystem"
          icon={<BoltIcon />}
        />
        <MetricCard
          label="Health (DALYs)"
          value={formatImpact(totals.dalys, "dalys")}
          variant="health"
          tone={dalysBurden ? "burden" : "normal"}
          subValue={dalysBurden ? "net burden" : undefined}
        />
        <MetricCard
          label="Ecosystem (PDFs)"
          value={formatImpact(totals.pdfs, "pdfs")}
          variant="ecosystem"
          tone={pdfsBurden ? "burden" : "normal"}
          subValue={pdfsBurden ? "net burden" : undefined}
        />
        <MetricCard
          label="Economic"
          value={formatImpact(totals.usd, "usd")}
          variant="economic"
          tone={usdBurden ? "burden" : "normal"}
          subValue={usdBurden ? "net burden" : undefined}
        />
      </div>
      {showRange && (
        <div className="mt-1 hidden gap-4 lg:grid lg:grid-cols-5">
          <div />
          <div />
          <div className="px-4">{kpiWhisker("dalys", "dalys")}</div>
          <div className="px-4">{kpiWhisker("pdfs", "pdfs")}</div>
          <div className="px-4">{kpiWhisker("usd", "usd")}</div>
        </div>
      )}
      {anyBurden && (
        <p className="mt-2 text-caption text-amber-400/80">
          Negative values indicate a net lifecycle burden — these projects
          primarily displace clean energy sources in their grid region.
        </p>
      )}
    </Section>
  );
}

function FolderIcon() {
  return (
    <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 7v10a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2h-6l-2-2H5a2 2 0 00-2 2z" />
    </svg>
  );
}

function BoltIcon() {
  return (
    <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
    </svg>
  );
}
